import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import Page from "../../Page";
import * as Icon from "react-bootstrap-icons";
import { ExclamationTriangle } from "react-bootstrap-icons";
import { useNavigate } from "react-router";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import Pagination from "../../../components/shared/Pagination";
import { 
  fetchListingData, 
  deleteJourney, 
  createJourney, 
  fetchSegments, 
  createSegment, 
  scheduleJourney,
  fetchMasterList,
  fetchTemplates,
  copyJourney
} from "../../../redux/actions/journeyAction";
import { fetchJourneyPartnerCategories, fetchPartnersAccessList as fetchPartnersAccessListAction } from "../../../redux/actions/actions";
import { toast } from "react-toastify";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import "./Custom.css";
// Function to get dynamic day interval based on journey data
const getDynamicDayInterval = (journey) => {
  if (journey && journey.total_days) {
    return `${journey.total_days}`;
  }
  return "1 Day"; // Default fallback
};

// Function to extract number of days from interval string
// Supports: n min/minutes, n hour/hours, n day/days, n week/weeks, n month/months
// Returns the equivalent number of days (as a decimal for accuracy)
const getDaysFromInterval = (interval, currentTime = null) => {
  if (!interval) return 1; // Default to 1 day if no interval
  
  const intervalStr = String(interval).trim();
  let days = 0;
  let hours = 0;
  let minutes = 0;
  
  // Pattern to match compound intervals like "3 days 5 hours 30 minutes"
  // This regex matches: number + whitespace + unit (days/hours/minutes/weeks/months)
  const compoundPattern = /(\d+(?:\.\d+)?)\s*(min|minute|minutes|hr|hour|hours|day|days|wk|week|weeks|mo|month|months)/gi;
  
  // Find all matches in the string
  const matches = intervalStr.matchAll(compoundPattern);
  let hasMatch = false;
  
  for (const match of matches) {
    hasMatch = true;
    const value = parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    
    // Extract days, hours, and minutes separately for accurate calculation
    if (unit === 'day' || unit === 'days') {
      days += value;
    } else if (unit === 'hr' || unit === 'hour' || unit === 'hours') {
      hours += value;
    } else if (unit === 'min' || unit === 'minute' || unit === 'minutes') {
      minutes += value;
    } else if (unit === 'wk' || unit === 'week' || unit === 'weeks') {
      days += value * 7;
    } else if (unit === 'mo' || unit === 'month' || unit === 'months') {
      days += value * 30.44;
    }
  }
  
  // If currentTime is provided, calculate actual days by adding interval to current time
  if (currentTime && hasMatch) {
    const currentDate = currentTime instanceof Date ? currentTime : new Date(currentTime);
    
    // Create a new date by adding the interval
    const resultDate = new Date(currentDate);
    resultDate.setDate(resultDate.getDate() + days);
    resultDate.setHours(resultDate.getHours() + hours);
    resultDate.setMinutes(resultDate.getMinutes() + minutes);
    
    // Get the date part only (ignoring time) for both start and end
    const startDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const endDateOnly = new Date(resultDate.getFullYear(), resultDate.getMonth(), resultDate.getDate());
    
    // Calculate the difference in days
    const timeDiff = endDateOnly.getTime() - startDateOnly.getTime();
    const daysDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24));
    
    // Return the number of days crossed (at least 1 if crossing any boundary or if explicit days > 0)
    return daysDiff > 0 ? daysDiff : (days > 0 ? days : 1);
  }
  
  // If compound pattern matched but no currentTime, calculate total days
  if (hasMatch) {
    // Convert hours and minutes to days
    const totalDays = days + (hours / 24) + (minutes / (24 * 60));
    return totalDays > 0 ? totalDays : 1; // Return at least 1 day if all values were 0
  }
  
  // Fallback: Try to match single unit patterns (for backward compatibility)
  const singlePatterns = [
    { regex: /(\d+(?:\.\d+)?)\s*(min|minute|minutes)\s*$/i, multiplier: 1 / (24 * 60) },
    { regex: /(\d+(?:\.\d+)?)\s*(hr|hour|hours)\s*$/i, multiplier: 1 / 24 },
    { regex: /(\d+(?:\.\d+)?)\s*(day|days)\s*$/i, multiplier: 1 },
    { regex: /(\d+(?:\.\d+)?)\s*(wk|week|weeks)\s*$/i, multiplier: 7 },
    { regex: /(\d+(?:\.\d+)?)\s*(mo|month|months)\s*$/i, multiplier: 30.44 },
  ];
  
  for (const pattern of singlePatterns) {
    const match = intervalStr.match(pattern.regex);
    if (match) {
      const value = parseFloat(match[1]);
      return value * pattern.multiplier;
    }
  }
  
  // If no unit found, try to parse as just a number (assume days)
  const numberMatch = intervalStr.match(/^(\d+(?:\.\d+)?)$/);
  if (numberMatch) {
    return parseFloat(numberMatch[1]);
  }
  
  // Default to 1 day if no match
  return 1;
};

// Helper function to get a human-readable interval string for display
const getIntervalDisplayString = (interval) => {
  if (!interval) return "1 day";
  
  const intervalStr = String(interval).trim();
  
  // If it already has a unit, return as is (capitalize first letter)
  const hasUnit = /(min|minute|minutes|hr|hour|hours|day|days|wk|week|weeks|mo|month|months)\s*$/i.test(intervalStr);
  if (hasUnit) {
    return intervalStr;
  }
  
  // If it's just a number, assume days
  const numberMatch = intervalStr.match(/^(\d+(?:\.\d+)?)$/);
  if (numberMatch) {
    const num = parseFloat(numberMatch[1]);
    return num === 1 ? "1 day" : `${num} days`;
  }
  
  return intervalStr;
};

const JourneyListing = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const journeyss = useSelector((state) => state.journey.journey_data || []);
  const loading = useSelector((state) => state.journey.loading);
  const pagination = useSelector((state) => state.journey.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 10,
    itemsPerPage: 10
  });

  const segmentsFromRedux = useSelector((state) => state.journey.segments || []);
  const segmentsLoading = useSelector((state) => state.journey.segments_loading);
  const masterListFromRedux = useSelector((state) => state.journey.master_list || []);
  const masterListLoading = useSelector((state) => state.journey.master_list_loading);
  const partnerCategoriesFromRedux = useSelector((state) => state.journey.partner_categories || []);
  const partnersListFromRedux = useSelector((state) => state.assetUpload.partnersList || []);
  const templates = useSelector((state) => state.templates.templates || []);
  const templatesLoading = useSelector((state) => state.templates.loading);
  
  // Copy journey states
  const copyJourneyLoading = useSelector((state) => state.journey.loading);
  const copyJourneyError = useSelector((state) => state.journey.error);
  const copyJourneySuccess = useSelector((state) => state.journey.copy_journey_success);

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [journeyToDelete, setJourneyToDelete] = useState(null);
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);
  const [showSegmentDialog, setShowSegmentDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [journeyToSchedule, setJourneyToSchedule] = useState(null);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [journeyToUnpublish, setJourneyToUnpublish] = useState(null);
  const [unpublishConfirmModalOpen, setUnpublishConfirmModalOpen] = useState(false);
  const [journeyToUnpublishConfirm, setJourneyToUnpublishConfirm] = useState(null);
  const [unpublishWarningModalOpen, setUnpublishWarningModalOpen] = useState(false);
  const [journeyToUnpublishWarning, setJourneyToUnpublishWarning] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'edit' or 'delete'
  const [stepsModalOpen, setStepsModalOpen] = useState(false);
  const [journeyToSteps, setJourneyToSteps] = useState(null);
  // Form states
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [journeyName, setJourneyName] = useState("");
  const [selectedSegment, setSelectedSegment] = useState("");
  const [segmentName, setSegmentName] = useState("");
  const [segmentDescription, setSegmentDescription] = useState("");
  const [selectedEmailType, setSelectedEmailType] = useState("AutoEmail");
  const [selectedPartnerCategory, setSelectedPartnerCategory] = useState("");
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedList, setSelectedList] = useState("");

  // Date and time states
  const [startDate, setStartDate] = useState(getStartDateTime().date);
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState(getStartDateTime().time);
  const [endTime, setEndTime] = useState("17:00");

  // Image handling states
  const [thumbnailData, setThumbnailData] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropConfig, setCropConfig] = useState({
    unit: 'px',
    width: 400,
    height: 400,
    aspect: 1
  });
  const [imageRef, setImageRef] = useState(null);
  const [crop, setCrop] = useState(null);
  const cropImageRef = useRef(null);

  const authToken = props.authToken;

  // Redux selectors
  const journeyLoading = useSelector((state) => state.journey.loading);
  const [publishing, setPublishing] = useState(false);
  const [creatingJourney, setCreatingJourney] = useState(false);

  // Helpers to avoid duplication
  const isAutoEmail = useMemo(() => (
    journeyToSchedule?.segment?.type === 'auto-email' &&
    journeyToSchedule?.stepData?.[0]?.node === 'Email'
  ), [journeyToSchedule]);

  const getMinimumStartTime = useCallback(() => {
    const now = new Date();
    // normalize to minute precision to match input resolution
    now.setSeconds(0, 0);
    return isAutoEmail ? new Date(now.getTime() + (4 * 60 * 60 * 1000)) : new Date(now);
  }, [isAutoEmail]);

  const formatLocalDateTime = useCallback((d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  }, []);

  function getStartDateTime() {
    const now = new Date();
    const isAutoEmail = (journeyToSchedule?.segment?.type === 'auto-email' && journeyToSchedule?.stepData?.[0]?.node === 'Email');

    let minimumStartTime;
    if (isAutoEmail) {
      // 4 hours and 2 minutes from now for auto-email journeys
      minimumStartTime = new Date(now.getTime() + (4 * 60 * 60 * 1000) + (2 * 60 * 1000));
    } else {
      // Exact current time for non-auto-email
      minimumStartTime = new Date(now);
    }

    return {
      date: minimumStartTime.toISOString().split('T')[0],
      time: minimumStartTime.toTimeString().slice(0, 5)
    };
  }

  // Recompute start date/time when segment type changes (auto-email <-> normal)
  useEffect(() => {
    if (!showScheduleDialog || !journeyToSchedule) return;
    const valid = getStartDateTime();
    setStartDate(valid.date);
    setStartTime(valid.time);
  }, [showScheduleDialog, journeyToSchedule?.segment?.type]);

  // Use segments from Redux if available, otherwise use default segments
  const segments = segmentsFromRedux.length > 0 ? segmentsFromRedux : [];

  const emailTypes = ["AutoEmail"];

  
  // Format partners list for ReactMultiSelectCheckboxes
  const partner = partnersListFromRedux.length > 0 ? partnersListFromRedux.map(
    ({ id: value, partner_category: label }) => ({
      value,
      label,
    })
  ) : [];

  // Use master list from Redux if available, otherwise use default lists
  const lists = masterListFromRedux.length > 0 ? masterListFromRedux : [
    { id: 1, name: "Customer List" },
    { id: 2, name: "Prospect List" },
    { id: 3, name: "Lead List" },
    { id: 4, name: "VIP List" },
    { id: 5, name: "Newsletter List" },
    { id: 6, name: "Event Attendees" },
    { id: 7, name: "Beta Testers" },
    { id: 8, name: "Support Team" }
  ];

  // Effects
  useEffect(() => {
    if (authToken) {
      dispatch(fetchListingData(authToken, pagination.currentPage, pagination.itemsPerPage));
      dispatch(fetchSegments(authToken,1,500,""));
      dispatch(fetchMasterList(authToken));
      dispatch(fetchJourneyPartnerCategories(authToken));
      dispatch(fetchPartnersAccessListAction(authToken));
      dispatch(fetchTemplates(authToken, {
        action: "select_template",
        filter: "all",
        search: "",
        page: 1,
        limit: 50
      }));
      // Clear any previous copy journey error on page load
      dispatch({ type: 'CLEAR_COPY_JOURNEY_ERROR' });
    }
  }, [dispatch, authToken, pagination.currentPage, pagination.itemsPerPage]);

  // Debug templates
  useEffect(() => {
    
  }, [templates, templatesLoading]);

  // Auto-select newly created segment when segments list is refreshed
  useEffect(() => {
    if (segmentsFromRedux.length > 0 && showJourneyDialog) {
      const newlyCreatedSegment = sessionStorage.getItem('newlyCreatedSegment');
      if (newlyCreatedSegment) {
        const newSegment = segmentsFromRedux.find(seg => seg.segment_name === newlyCreatedSegment);
        if (newSegment) {
          setSelectedSegment(newSegment.id);
          sessionStorage.removeItem('newlyCreatedSegment'); // Clean up
        }
      }
    }
  }, [segmentsFromRedux, showJourneyDialog]);

  useEffect(() => {
    if (showScheduleDialog) {
      const now = new Date();
      
      const isAutoEmail = (journeyToSchedule?.segment?.type === 'auto-email' && journeyToSchedule?.stepData?.[0]?.node === 'Email');
      let fourHoursLater;
      if (isAutoEmail) {
        fourHoursLater = new Date(now.getTime() + (4 * 60 * 60 * 1000));
      } else {
        fourHoursLater = new Date(now.getTime());
      }

      setTimeout(() => {
        const startInput = document.getElementById('start-datetime-input');
        if (startInput) {
          const year = fourHoursLater.getFullYear();
          const month = String(fourHoursLater.getMonth() + 1).padStart(2, '0');
          const day = String(fourHoursLater.getDate()).padStart(2, '0');
          const hours = String(fourHoursLater.getHours()).padStart(2, '0');
          const minutes = String(fourHoursLater.getMinutes()).padStart(2, '0');
          const minDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
          
          startInput.min = minDateTime;
        }
        
        const endInput = document.getElementById('end-datetime-input');
        if (endInput) {
          // Calculate minimum end date based on start date + dynamic dayinterval
          const startDateTime = new Date(`${startDate}T${startTime}`);
          const dynamicDayInterval = getDynamicDayInterval(journeyToSchedule);
          const daysToAdd = getDaysFromInterval(dynamicDayInterval, startDateTime);
          
          // Convert days to milliseconds for accurate date calculation (handles minutes/hours correctly)
          const daysToAddMs = daysToAdd * 24 * 60 * 60 * 1000;
          const minEndDateFromStart = new Date(startDateTime.getTime() + daysToAddMs);
          
          // Set the minimum constraint to the later of the two dates
          const minDateTimeForEnd = new Date(Math.max(fourHoursLater.getTime(), minEndDateFromStart.getTime()));
          
          // Use local timezone instead of UTC to avoid date shifting
          const year = minDateTimeForEnd.getFullYear();
          const month = String(minDateTimeForEnd.getMonth() + 1).padStart(2, '0');
          const day = String(minDateTimeForEnd.getDate()).padStart(2, '0');
          const hours = String(minDateTimeForEnd.getHours()).padStart(2, '0');
          const minutes = String(minDateTimeForEnd.getMinutes()).padStart(2, '0');
          endInput.min = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }, 100);
    }
  }, [showScheduleDialog, startDate, startTime, journeyToSchedule]);

  // Handle copy journey success/error
  useEffect(() => {
    
    if (copyJourneySuccess) {
      
      toast.success("Journey copied successfully!");
      // Reset the success flag
      dispatch({ type: 'RESET_COPY_JOURNEY_SUCCESS' });
      // Refresh the journey list
      dispatch(fetchListingData(authToken, pagination.currentPage, pagination.itemsPerPage));
    }
    if (copyJourneyError) {
      toast.error(copyJourneyError.message || "Failed to copy journey");
    }
  }, [copyJourneySuccess, copyJourneyError, dispatch, authToken, pagination.currentPage, pagination.itemsPerPage]);

  // Helper function to check if journey is published or scheduled
  const isJourneyPublishedOrScheduled = (journey) => {
    return journey.visibility_status === 'published' || journey.visibility_status === 'scheduled';
  };

  // Event handlers
  const handleDeleteClick = (journey) => {
    if (isJourneyPublishedOrScheduled(journey)) {
      setJourneyToUnpublishWarning(journey);
      setPendingAction('delete');
      setUnpublishWarningModalOpen(true);
    } else {
      setJourneyToDelete(journey);
      setDeleteModalOpen(true);
    }
  };

  const handleEditClick = (journey) => {
    if (isJourneyPublishedOrScheduled(journey)) {
      setJourneyToUnpublishWarning(journey);
      setPendingAction('edit');
      setUnpublishWarningModalOpen(true);
    } else {
      navigate(`/journey/builder/${journey.id}`);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    dispatch(fetchListingData(authToken, page, pagination.itemsPerPage));
  };

  const handleItemsPerPageChange = (itemsPerPage) => {
    dispatch(fetchListingData(authToken, 1, itemsPerPage)); // Reset to page 1 when changing items per page
  };


    const handleCopyClick = (journey) => {
      dispatch({ type: 'CLEAR_COPY_JOURNEY_ERROR' });
      const journeyData = {
        id: journey.id,
        name: journey.name,
      };
      dispatch(copyJourney(authToken, journeyData));
    };

    const selectPartnerHandler = (value, event) => {
    if (event.action === "select-option" && event.option.value === "*") {
      setSelectedPartners(partner);
    } else if (
      event.action === "deselect-option" &&
      event.option.value === "*"
    ) {
      setSelectedPartners([]);
    } else if (event.action === "deselect-option") {
      value.filter((o) => o.value !== "*");
      setSelectedPartners(value.map(({ value }) => value));
    } else if (value.length === partner.length - 1) {
      setSelectedPartners(partner);
    } else {
      setSelectedPartners(value.map(({ value }) => value));
    }
  };

  const handleDeleteConfirm = async () => {
    if (journeyToDelete && authToken) {
      // show loader while deleting
      dispatch({ type: 'TOGGLE_LOADING', payload: true });
      await dispatch(deleteJourney(authToken, journeyToDelete.id));
      await dispatch(fetchListingData(authToken)); // fetch updated list after deletion
      dispatch({ type: 'TOGGLE_LOADING', payload: false });
    }
    setDeleteModalOpen(false);
  };

  // Image handling functions
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          // Store the original image dimensions
          setImageRef({
            dataUrl: reader.result,
            fileName: file.name,
            width: img.width,
            height: img.height
          });
          setShowCropper(true);
          
          const smallerDimension = Math.min(img.width, img.height);
          const x = (img.width - smallerDimension) / 2;
          const y = (img.height - smallerDimension) / 2;
          setCrop({
            unit: 'px',
            x: x,
            y: y,
            width: smallerDimension,
            height: smallerDimension,
            aspect: 1
          });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (crop) => {
    
    // Validate crop data
    if (!imageRef) {
      toast.error("No image loaded");
      return;
    }
    
    if (!crop || !crop.width || !crop.height || crop.width <= 0 || crop.height <= 0) {
      toast.error("Please select a valid crop area first");
      return;
    }
    
    if (crop.x === undefined || crop.y === undefined) {
      toast.error("Invalid crop coordinates");
      return;
    }
    
    getCroppedImg(imageRef, crop);
  };

  const getCroppedImg = (imageSrc, crop) => {
    
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageSrc.dataUrl;

    image.onload = () => {
      
      const canvas = document.createElement('canvas');
      const targetSize = 400;
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');

      let displayedImage = cropImageRef.current;
      
      if (!displayedImage) {
        displayedImage = document.querySelector('.react-crop img');
        if (!displayedImage) {
          displayedImage = document.querySelector('img[src*="data:image"]');
        }
      }
      
      if (!displayedImage) {
        toast.error("Image element not found");
        return;
      }


       const scaleX = image.naturalWidth / displayedImage.offsetWidth;
       const scaleY = image.naturalHeight / displayedImage.offsetHeight;

       // Calculate the actual crop dimensions
       let cropX = Math.max(0, Math.round(crop.x * scaleX));
       let cropY = Math.max(0, Math.round(crop.y * scaleY));
       let cropWidth = Math.min(Math.round(crop.width * scaleX), image.naturalWidth - cropX);
       let cropHeight = Math.min(Math.round(crop.height * scaleY), image.naturalHeight - cropY);

             // Validate final crop dimensions
       if (cropWidth <= 0 || cropHeight <= 0) {
         toast.error("Please select a valid crop area within the image boundaries");
         return;
       }

      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      try {
        // Draw the cropped image
        ctx.drawImage(
          image,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          targetSize,
          targetSize
        );

        // Convert to blob
        canvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            
            reader.onloadend = () => {
              const base64data = reader.result;
              
              const thumbnailData = {
                base64: base64data,
                file: {
                  name: imageSrc.fileName,
                  size: blob.size,
                  type: blob.type
                }
              };
              
              setThumbnailData(thumbnailData);
              setThumbnail(URL.createObjectURL(blob));
              setShowCropper(false);
              toast.success("Image cropped successfully!");
            };
          } else {
            toast.error("Failed to process image");
          }
        }, 'image/jpeg', 0.9);
      } catch (error) {
        toast.error("Error processing image");
      }
    };

    image.onerror = (error) => {
      toast.error("Error loading image");
    };
  };

  const handleCreateJourney = async () => {
    if (creatingJourney) return;
    if (!journeyName.trim()) {
      toast.error("Please enter a journey name");
      return;
    }

    if (!selectedSegment) {
      toast.error("Please select a segment");
      return;
    }
    
    try {
      setCreatingJourney(true);
      const journeyData = {
        title: journeyName,
        segment: selectedSegment,
        thumbnail: thumbnailData
      };
      
      await dispatch(createJourney(authToken, "", journeyData));
      
      setJourneyName("");
      setSelectedSegment("");
      setThumbnail(null);
      setThumbnailData(null);
      setShowJourneyDialog(false);
      
      // Refresh the journey list
      await dispatch(fetchListingData(authToken));
    } catch (error) {
      toast.error("Failed to create journey");
    } finally {
      setCreatingJourney(false);
    }
  };

  const handleSubmitSegment = async () => {
    if (selectedPartners.length === 0) {
      toast.error("Please select partner categories");
      return;
    }

    if (!selectedList) {
      toast.error("Please select a list");
      return;
    }
    
    try {
      // Get selected partner category names from the partner array using the selected IDs
      const selectedPartnerNames = selectedPartners.map(partnerId => {
        const partnerObj = partner.find(p => p.value === partnerId);
        return partnerObj ? partnerObj.label : partnerId;
      }).join(", ");
      
      const selectedListObj = lists.find(list => list.id === parseInt(selectedList));
      
      const segmentData = {
        action: 'create',
        id: null,
        segment_name: `${selectedEmailType}`,
        partner_category: selectedPartners,
        master_data_list: parseInt(selectedList) || 1,
        channel: null,
        source: null,
        filters: null,
        page_name:'journey'
      };
      
      const createdSegmentName = segmentData.segment_name;
      
      await dispatch(createSegment(authToken, segmentData));
      setSelectedEmailType("AutoEmail");
      setSelectedPartnerCategory("");
      setSelectedPartners([]);
      setSelectedList("");
      setShowSegmentDialog(false);
      
      await dispatch(fetchSegments(authToken));
      setShowJourneyDialog(true);
      sessionStorage.setItem('newlyCreatedSegment', createdSegmentName);
    } catch (error) {
      toast.error("Failed to create segment");
    }
  };

  const handleSchedulePublish = async (isPublish = true) => {
    if (publishing) return;
    setPublishing(true);
    if (!startDate) {
      toast.error("Please select a start date");
      setPublishing(false);
      return;
    }

    if (!endDate) {
      toast.error("Please select an end date");
      setPublishing(false);
      return;
    }

    if (!startTime) {
      toast.error("Please select a start time");
      setPublishing(false);
      return;
    }

    if (!endTime) {
      toast.error("Please select an end time");
      setPublishing(false);
      return;
    }

    if (!journeyToSchedule) {
      toast.error("No journey selected for scheduling");
      setPublishing(false);
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    const minimumStartTime = getMinimumStartTime();

    // Enforce minimum start time based on journey type
    if (startDateTime < minimumStartTime) {
      toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
      setPublishing(false);
      return;
    }

    if (startDateTime > endDateTime) {
      toast.error("End date/time must be after start date/time");
      setPublishing(false);
      return;
    }
    
    try {
      const scheduleData = {
        start_date: `${startDate} ${startTime}:00`,
        end_date: `${endDate} ${endTime}:00`,
        publish: isPublish ? false : false
      };
      
      
      await dispatch(scheduleJourney(authToken, journeyToSchedule.id, scheduleData));
      setPublishing(false);
      
      // Refresh the journey list to show updated status
      await dispatch(fetchListingData(authToken, pagination.currentPage, pagination.itemsPerPage));
      
      // Reset form
      setStartDate(getStartDateTime().date);
      setEndDate("");
      setStartTime(getStartDateTime().time);
      setEndTime("17:00");
      setShowScheduleDialog(false);
      setJourneyToSchedule(null);
    } catch (error) {
      toast.error("Failed to schedule journey");
      setPublishing(false);
    }
  };

  const handleUnpublishClick = () => {
    setJourneyToUnpublish(journeyToSchedule);
    setUnpublishModalOpen(true);
  };

  const handleUnpublishIconClick = (journey) => {
    setJourneyToUnpublishConfirm(journey);
    setUnpublishConfirmModalOpen(true);
  };

  const handleUnpublishIconConfirm = async () => {
    if (journeyToUnpublishConfirm && authToken) {
      try {
        dispatch({ type: 'TOGGLE_LOADING', payload: true });
        const scheduleData = {
          start_date: "",
          end_date: "",
          publish: false
        };
        
        await dispatch(scheduleJourney(authToken, journeyToUnpublishConfirm.id, scheduleData));
        
        // Refresh the journey list to show updated status
        await dispatch(fetchListingData(authToken, pagination.currentPage, pagination.itemsPerPage));
        dispatch({ type: 'TOGGLE_LOADING', payload: false });
      } catch (error) {
        toast.error("Failed to unpublish journey");
        dispatch({ type: 'TOGGLE_LOADING', payload: false });
      }
    }
    setUnpublishConfirmModalOpen(false);
    setJourneyToUnpublishConfirm(null);
  };

  const handleUnpublishConfirm = async () => {
    if (publishing) return;
    setPublishing(true);
    if (!startDate) {
      toast.error("Please select a start date");
      setPublishing(false);
      return;
    }

    if (!endDate) {
      toast.error("Please select an end date");
      setPublishing(false);
      return;
    }

    if (!startTime) {
      toast.error("Please select a start time");
      setPublishing(false);
      return;
    }

    if (!endTime) {
      toast.error("Please select an end time");
      setPublishing(false);
      return;
    }

    if (!journeyToUnpublish) {
      toast.error("No journey selected for scheduling");
      setPublishing(false);
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    const minimumStartTime2 = getMinimumStartTime();
 
    if (startDateTime < minimumStartTime2) {
      toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
      setPublishing(false);
      return;
    }

    if (startDateTime > endDateTime) {
      toast.error("End date/time must be after start date/time");
      setPublishing(false);
      return;
    }
    
    try {
      const scheduleData = {
        start_date: `${startDate} ${startTime}:00`,
        end_date: `${endDate} ${endTime}:00`,
        publish: false
      };
      
      
      await dispatch(scheduleJourney(authToken, journeyToUnpublish.id, scheduleData));
      // Refresh the journey list to show updated status
      await dispatch(fetchListingData(authToken, pagination.currentPage, pagination.itemsPerPage));
      setPublishing(false);
    } catch (error) {
      toast.error("Failed to unpublish journey");
      setPublishing(false);
    }
  };

  props.pageTypeHandler("Journey");

  return (
    <>
      <style>
        {`
           .header-div div { margin-top: 0px !IMPORTANT; }
              
        `}
      </style>
      <div className="container mt-4 maindiv">
      <div className="container-fluid mt-4">
        {/* Header */}
        <Row className="mb-3">
          <Col>
            <h2 className="mb-0">Journey Listing</h2>
          </Col>
                     <Col xs="auto">
             <Button 
               variant="primary" 
               onClick={() => setShowJourneyDialog(true)}
               className="d-flex align-items-center gap-2 me-2"
             >
               <Icon.Plus /> New Journey
             </Button>
             
           </Col>
        </Row>
      </div>

      <Page title="Journey">
        {/* Table Header */}
        <div className="row fw-bold border-bottom py-2 bg-light">
          <div className="col-2">Img</div>
          <div className="col-2">Journey Name</div>
          <div className="col-2">Segments</div>
          <div className="col-2">Status</div>
          <div className="col-2">Schedule/Published Date</div>
          <div className="col-2 text-end text-muted">Actions</div>
        </div>

        {/* Table Data */}
        {loading ? (
          <div className="text-center py-3">Loading...</div>
        ) : journeyss && journeyss.length > 0 ? (
          journeyss.map((journey) => (
            <div key={journey.id} className="row align-items-center border-bottom py-2">
              <div className="col-2">
                {journey.thumbnail ? (
                  <img 
                    src={journey.thumbnail} 
                    alt="thumb" 
                    className="rounded-circle"
                    style={{ width: 50, height: 50, objectFit: 'cover' }}
                  />
                ) : (
                  <div 
                    className="bg-warning rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                    style={{ width: 50, height: 50 }}
                  >
                    {journey.title ? journey.title.charAt(0).toUpperCase() : 'J'}
                  </div>
                )}
              </div>
              <div className="col-2">
                <div 
                  className="fw-medium cursor-pointer"
                  onClick={() => handleEditClick(journey)}
                  style={{ cursor: 'pointer' }}
                >
                  {journey.title}
                </div>
              </div>
              <div className="col-2">
                {journey.segment && typeof journey.segment === 'object' ? journey.segment.segment_name : journey.segment || 'No Segment'}
              </div>
              <div className="col-2">
                <span className={`badge bg-${journey.visibility_status === 'published' ? 'success' : journey.visibility_status === 'scheduled' ? 'warning' : 'danger'}`}>
                {journey.visibility_status ? journey.visibility_status.charAt(0).toUpperCase() + journey.visibility_status.slice(1) : 'Draft'}
                </span>
              </div>
              <div className="col-2">
                {journey.start_date ? journey.start_date : ''}
              </div>
              <div className="col-2 d-flex justify-content-end align-items-center text-muted small">
                {(() => {
                  const now = new Date();
                 
                  const isPublished = journey.published === "1";

                  return (
                    <>
                      {/* View */}
                      <span
                        className="me-3"
                        role="button"
                        title="View Journey Steps"
                        onClick={() => {
                          setJourneyToSteps(journey.stepData);
                          setStepsModalOpen(true);
                        }}
                      >
                        <Icon.Eye className="text-secondary" size={17} />
                      </span>

                         {(journey.status === "1" || journey.status === "4" ) && (
                        <span
                          className="me-3"
                          role="button"
                          title="Scheduled"
                          onClick={() => {
                            setJourneyToSchedule(journey);
                            setShowScheduleDialog(true);
                          }}
                        >
                          <Icon.Clock className="text-info" size={17} />
                        </span>
                      )}

                      {(journey.status === "2" || isPublished) && (
                        <span
                          className="me-3"
                          role="button"
                          title="Un-Published"
                          onClick={() => handleUnpublishIconClick(journey)}
                        >
                          <Icon.SlashCircle className="text-warning" size={17} />
                        </span>
                      )}

                     {/* Edit */}
                      {(journey.status === "1"  || journey.status === "4") && (
                        <span
                          className="me-3"
                          role="button"
                          title="Edit Journey"
                          onClick={() => handleEditClick(journey)}
                        >
                          <Icon.PencilSquare className="text-primary" size={17} />
                        </span>
                      )}

                      {/* Copy */}
                      <span
                        className="me-3"
                        role="button"
                        title="Copy Journey"
                        onClick={() => handleCopyClick(journey)}
                      >
                        <Icon.Copy className="text-secondary" size={17} />
                      </span>

                      {/* Delete */}
                      {(journey.status === "1" || journey.status === "4") && (
                        <span
                          role="button"
                          title="Delete Journey"
                          onClick={() => handleDeleteClick(journey)}
                        >
                          <Icon.Trash className="text-danger" size={17} />
                        </span>
                      )}
                    </>
                  );
                })()}

              </div>

            </div>
          ))
        ) : (
          <div className="text-center py-3">No journeys found</div>
        )}

        {/* Pagination */}
        {!loading && journeyss && journeyss.length > 0 && (
          <div style={{marginTop: '5px'}}>
          
            <Pagination
              currentPage={pagination.currentPage || 1}
              totalPages={pagination.totalPages || 1}
              totalItems={pagination.totalItems || journeyss.length}
              itemsPerPage={pagination.itemsPerPage || 10}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showInfo={true}
            />
          </div>
        )}
      </Page>

      {/* Create Journey Modal */}
      <Modal show={showJourneyDialog} onHide={() => setShowJourneyDialog(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Journey</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Thumbnail</Form.Label>
                  {!thumbnail ? (
                    <div 
                      className="border border-dashed border-secondary rounded p-4 text-center cursor-pointer"
                      onClick={() => document.getElementById('thumbnail-upload').click()}
                      style={{ cursor: 'pointer' }}
                    >
                      <Icon.Image size={32} className="text-muted mb-2" />
                      <div className="text-muted">Upload Image</div>
                      <div className="text-muted small">400x400px</div>
                      <input 
                        id="thumbnail-upload"
                        type="file" 
                        className="d-none" 
                        accept="image/*" 
                        onChange={handleImageUpload} 
                      />
                    </div>
                                     ) : (
                     <div className="position-relative">
                       <img 
                         src={thumbnail} 
                         alt="Thumbnail" 
                         className="img-fluid rounded border"
                         style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                       />
                       <Button
                         variant="danger"
                         size="sm"
                         className="position-absolute top-0 end-0 m-1"
                         onClick={() => {
                           setThumbnail(null);
                           setThumbnailData(null);
                         }}
                       >
                         <Icon.X />
                       </Button>
                     </div>
                   )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Journey Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={journeyName}
                    onChange={(e) => setJourneyName(e.target.value)}
                    placeholder="Enter journey name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Segment</Form.Label>
                  <Form.Select
                    value={selectedSegment}
                    onChange={(e) => setSelectedSegment(e.target.value)}
                  >
                    <option value="">Select Segment</option>
                    {segments.map((segment, index) => {
                      // Handle null, object format (from Redux), and string format (fallback)
                      if (!segment) return null; // Skip null segments
                      
                      const segmentId = typeof segment === 'object' ? segment.id : segment;
                      const segmentName = typeof segment === 'object' ? segment.segment_name : segment;
                      const key = segmentId || `segment-${index}`;
                      
                      return (
                        <option key={key} value={segmentId}>
                          {segmentName}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJourneyDialog(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateJourney} disabled={creatingJourney}>
            {creatingJourney ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              'Create Journey'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Segment Modal */}
      <Modal show={showSegmentDialog} onHide={() => {
        setShowSegmentDialog(false);
        setSelectedPartners([]);
        setSelectedEmailType("AutoEmail");
        setSelectedList("");
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Segment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium text-dark mb-2">
                    Email Type
                  </Form.Label>
                  <Form.Select
                    value={selectedEmailType}
                    onChange={(e) => setSelectedEmailType(e.target.value)}
                    className="form-select-custom"
                    style={{ width: '100%', maxWidth: '100%' }}
                  >
                    {emailTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium text-dark mb-2">
                    Select Partner Category
                  </Form.Label>
                  <div 
                    className="select-box-w100" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '100%', 
                      display: 'block',
                      position: 'relative'
                    }}
                  >
                    <div 
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        display: 'block',
                        position: 'relative'
                      }}
                    >
                      <ReactMultiSelectCheckboxes
                        options={partner}
                        placeholderButtonLabel={
                          partnersListFromRedux.length === 0 
                            ? "Loading Partner Categories..." 
                            : selectedPartners?.length > 0 
                              ? `${selectedPartners.length} selected` 
                              : "Select Partner Category"
                        }
                        defaultValue={selectedPartners}
                        values={selectedPartners}
                        setState={setSelectedPartners}
                        onChange={selectPartnerHandler}
                        disabled={partnersListFromRedux.length === 0}
                        style={{ 
                          width: '100%', 
                          maxWidth: '100%', 
                          display: 'block',
                          position: 'relative'
                        }}
                      />
                    </div>
                  </div>
                  {partnersListFromRedux.length === 0 && (
                    <div className="text-muted small mt-1">
                      <Icon.ArrowClockwise className="me-1" />
                      Loading partner categories...
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium text-dark mb-2">
                    Select List
                  </Form.Label>
                  <Form.Select
                    value={selectedList}
                    onChange={(e) => setSelectedList(e.target.value)}
                    className="form-select-custom"
                    style={{ width: '100%', maxWidth: '100%' }}
                  >
                    <option value="">Select List</option>
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>{list.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setShowJourneyDialog(true);
              
              setSelectedPartners([]);
              setSelectedEmailType("AutoEmail");
              setSelectedList("");
              setShowSegmentDialog(false);
              
            }}
            className="px-4 py-2"
          >
            Back to Journey
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitSegment}
            className="px-4 py-2"
            style={{
              backgroundColor: '#17a2b8',
              borderColor: '#17a2b8'
            }}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

             {/* Delete Confirmation Modal */}
       <Modal show={deleteModalOpen} onHide={() => setDeleteModalOpen(false)} centered>
         <Modal.Header closeButton>
           <Modal.Title>Delete Journey</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <div className="d-flex align-items-start">
             <div className="bg-danger-subtle rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
               <ExclamationTriangle className="text-danger" size={20} />
             </div>
             <div>Are you sure you want to delete this journey? This action cannot be undone.</div>
           </div>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
             Delete
           </Button>
           <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
             Cancel
           </Button>
         </Modal.Footer>
       </Modal>

       {/* Unpublish Warning Modal */}
       <Modal show={unpublishWarningModalOpen} onHide={() => setUnpublishWarningModalOpen(false)} centered>
         <Modal.Header closeButton>
           <Modal.Title>Journey Cannot Be Modified</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <div className="d-flex align-items-start">
             <div className="bg-warning-subtle rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
               <ExclamationTriangle className="text-warning" size={20} />
             </div>
             <div>
               <strong>Journey:</strong> {journeyToUnpublishWarning?.title}
               <br />
               <br />
               This journey is currently {journeyToUnpublishWarning?.visibility_status}. 
               <br />
               <strong>Please unpublish before {pendingAction === 'edit' ? 'editing' : 'deleting'}.</strong>
             </div>
           </div>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={() => setUnpublishWarningModalOpen(false)}>
             OK
           </Button>
         </Modal.Footer>
       </Modal>

               {/* Unpublish Confirmation Modal */}
        <Modal show={unpublishModalOpen} onHide={() => setUnpublishModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Unpublish Journey</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex align-items-start">
              <div className="bg-warning-subtle rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <ExclamationTriangle className="text-warning" size={20} />
              </div>
              <div>
                Are you sure you want to unpublish this journey? 
                <br />
                <strong>Journey:</strong> {journeyToUnpublish?.title}
                <br />
                <small className="text-muted">This will set the journey status to unpublished.</small>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="warning" onClick={handleUnpublishConfirm} disabled={publishing}>
              Unpublish
            </Button>
            <Button variant="secondary" onClick={() => setUnpublishModalOpen(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Unpublish Icon Confirmation Modal */}
        <Modal show={unpublishConfirmModalOpen} onHide={() => setUnpublishConfirmModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Unpublish Journey</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex align-items-start">
              <div className="bg-warning-subtle rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <ExclamationTriangle className="text-warning" size={20} />
              </div>
                             <div>
                 Are you sure you want to unpublish this journey? 
                 <br />
                 <strong>Journey:</strong> {journeyToUnpublishConfirm?.title}
                 <br />
                 <small className="text-muted">This will set the journey status to unpublished immediately.</small>
               </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
                         <Button variant="warning" onClick={handleUnpublishIconConfirm} disabled={publishing}>
               Unpublish
             </Button>
            <Button variant="secondary" onClick={() => setUnpublishConfirmModalOpen(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

             {/* Image Cropper Modal */}
       {showCropper && (
         <Modal show={showCropper} onHide={() => setShowCropper(false)} size="lg">
           <Modal.Header closeButton>
             <Modal.Title>Crop Image</Modal.Title>
           </Modal.Header>
           <Modal.Body>
             <div className="text-center">
                                                              <div className="mb-3">
                   <small className="text-muted">
                     Click and drag on the image to create or adjust the crop area. Drag the corners to resize or drag the center to move the selection.
                   </small>
                 </div>
                               <div className="border rounded p-3 bg-light">
                                                         {!crop && (
                      <div className="text-center mb-2">
                        <small className="text-info">
                          Loading crop area...
                        </small>
                      </div>
                    )}
                    {crop && (
                      <div className="text-center mb-2">
                        <small className="text-success">
                          Crop area ready! Width: {crop.width}, Height: {crop.height}
                        </small>
                      </div>
                    )}
                                                       <ReactCrop
                    crop={crop}
                    onChange={c => { setCrop(c); }}
                    aspect={1}
                    minWidth={50}
                    minHeight={50}
                    className="img-fluid"
                    style={{ 
                      maxHeight: '400px',
                      cursor: 'crosshair'
                    }}
                  >
                      <img 
                        ref={cropImageRef}
                        src={imageRef && imageRef.dataUrl} 
                        alt="Crop me" 
                        className="img-fluid"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '400px', 
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto',
                          userSelect: 'none',
                          pointerEvents: 'auto',
                          touchAction: 'none',
                          cursor: 'crosshair'
                        }}
                        draggable={false}
                        onLoad={() => {}}
                      />
                  </ReactCrop>
                </div>
             </div>
           </Modal.Body>
           <Modal.Footer>
             <Button variant="secondary" onClick={() => setShowCropper(false)}>
               Cancel
             </Button>
                           <Button 
                variant="primary" 
                onClick={() => handleCropComplete(crop)}
                disabled={!crop}
              >
                Crop Image
              </Button>
           </Modal.Footer>
         </Modal>
       )}

      {/* Schedule/Publish Modal */}
      <Modal 
        show={showScheduleDialog} 
        onHide={() => {
          setShowScheduleDialog(false);
          setJourneyToSchedule(null);
        }} 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Schedule/ Publish
            {journeyToSchedule && (
               <div className="text-muted small mt-1">
                 Journey: {journeyToSchedule.title} | Duration: {getDynamicDayInterval(journeyToSchedule)}
               </div>
             )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date & Time</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="datetime-local"
                      value={`${startDate}T${startTime}`}
                      onChange={(e) => {
                        const [date, time] = e.target.value.split('T');
                        if (!date || !time) return;
                        
                        const selectedDateTime = new Date(`${date}T${time}`);
                        const minimum = getMinimumStartTime();
                        
                        // Validate start for auto-email (4 hours) and non-auto-email (current time)
                        if (selectedDateTime < minimum) {
                          toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
                          // Reset to valid time
                          const validDateTime = getStartDateTime();
                          setStartDate(validDateTime.date);
                          setStartTime(validDateTime.time);
                          // Update the input value to reflect the corrected time
                          e.target.value = `${validDateTime.date}T${validDateTime.time}`;
                          return;
                        }
                        
                        setStartDate(date);
                        setStartTime(time);
                      }}
                      placeholder="Start Date & Time"
                      id="start-datetime-input"
                      min={formatLocalDateTime(getMinimumStartTime())}
                      step="900"
                      onFocus={(e) => {
                        // Set minimum constraint when input is focused
                        e.target.min = formatLocalDateTime(getMinimumStartTime());
                      }}
                      onBlur={(e) => {
                        // Additional validation on blur
                        const [date, time] = e.target.value.split('T');
                        if (date && time) {
                          const selectedDateTime = new Date(`${date}T${time}`);
                          const minimum = getMinimumStartTime();
                          if (selectedDateTime < minimum) {
                            toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
                            const validDateTime = getStartDateTime();
                            setStartDate(validDateTime.date);
                            setStartTime(validDateTime.time);
                            e.target.value = `${validDateTime.date}T${validDateTime.time}`;
                          }
                        }
                      }}
                    />
                    
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date & Time</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="datetime-local"
                      value={`${endDate}T${endTime}`}
                      onChange={(e) => {
                        const [date, time] = e.target.value.split('T');
                        const selectedDateTime = new Date(`${date}T${time}`);
                        const startDateTime = new Date(`${startDate}T${startTime}`);
                        const minimum = getMinimumStartTime();
                        
                        // Calculate minimum end date based on start date + dynamic dayinterval
                        const dynamicDayInterval = getDynamicDayInterval(journeyToSchedule);
                        const daysToAdd = getDaysFromInterval(dynamicDayInterval);
                        
                        // Create the exact end date by adding interval to the start date
                        // Convert days to milliseconds for accurate calculation (handles minutes/hours correctly)
                        const daysToAddMs = daysToAdd * 24 * 60 * 60 * 1000;
                        const minEndDateFromStart = new Date(startDateTime.getTime() + daysToAddMs);
                        // Validate end not earlier than min and after start
                        if (selectedDateTime < minimum) {
                          toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
                          setEndDate("");
                          setEndTime("17:00");
                          return;
                        }
                        
                        if (selectedDateTime <= startDateTime) {
                          toast.error("End time must be after start time");
                          setEndDate("");
                          setEndTime("17:00");
                          return;
                        }
                        // Check if end datetime is at least the dynamic dayinterval from start datetime
                        // Compare full datetime (not just date) to handle cases where time matters
                        if (selectedDateTime < minEndDateFromStart) {
                          toast.error(`End date must be at least ${getIntervalDisplayString(dynamicDayInterval)} after the start date`);
                          setEndDate("");
                          setEndTime("17:00");
                          return;
                        }
                       
                       setEndDate(date);
                       setEndTime(time || '17:00');
                      }}
                      placeholder="End Date & Time"
                      id="end-datetime-input"
                        min={(() => {
                         const startDateTime = new Date(`${startDate}T${startTime}`);
                         const dynamicDayInterval = getDynamicDayInterval(journeyToSchedule);
                         const daysToAdd = getDaysFromInterval(dynamicDayInterval);
                         
                         // Create the exact end date by adding interval to the start date
                         // Convert days to milliseconds for accurate calculation (handles minutes/hours correctly)
                         const daysToAddMs = daysToAdd * 24 * 60 * 60 * 1000;
                         const minEndDateFromStart = new Date(startDateTime.getTime() + daysToAddMs);
                         
                         // Use local timezone instead of UTC to avoid date shifting
                         const year = minEndDateFromStart.getFullYear();
                         const month = String(minEndDateFromStart.getMonth() + 1).padStart(2, '0');
                         const day = String(minEndDateFromStart.getDate()).padStart(2, '0');
                         const hours = String(minEndDateFromStart.getHours()).padStart(2, '0');
                         const minutes = String(minEndDateFromStart.getMinutes()).padStart(2, '0');
                         return `${year}-${month}-${day}T${hours}:${minutes}`;
                       })()}
                      step="900"
                      onFocus={(e) => {
                         // Set minimum constraint when input is focused
                         const startDateTime = new Date(`${startDate}T${startTime}`);
                         const dynamicDayInterval = getDynamicDayInterval(journeyToSchedule);
                         const daysToAdd = getDaysFromInterval(dynamicDayInterval);
                         
                         // Create the exact end date by adding interval to the start date
                         // Convert days to milliseconds for accurate calculation (handles minutes/hours correctly)
                         const daysToAddMs = daysToAdd * 24 * 60 * 60 * 1000;
                         const minEndDateFromStart = new Date(startDateTime.getTime() + daysToAddMs);
                         // Use local timezone instead of UTC to avoid date shifting
                         const year = minEndDateFromStart.getFullYear();
                         const month = String(minEndDateFromStart.getMonth() + 1).padStart(2, '0');
                         const day = String(minEndDateFromStart.getDate()).padStart(2, '0');
                         const hours = String(minEndDateFromStart.getHours()).padStart(2, '0');
                         const minutes = String(minEndDateFromStart.getMinutes()).padStart(2, '0');
                         e.target.min = `${year}-${month}-${day}T${hours}:${minutes}`;
                       }}
                      onBlur={(e) => {
                        // Additional validation on blur
                        const [date, time] = e.target.value.split('T');
                        if (date && time) {
                          const selectedDateTime = new Date(`${date}T${time}`);
                          const startDateTime = new Date(`${startDate}T${startTime}`);
                          const minimum = getMinimumStartTime();
                          
                                                     // Calculate minimum end date based on start date + dynamic dayinterval
                           const dynamicDayInterval = getDynamicDayInterval(journeyToSchedule);
                           const daysToAdd = getDaysFromInterval(dynamicDayInterval);
                           
                           // Create the exact end date by adding interval to the start date
                           // Convert days to milliseconds for accurate calculation (handles minutes/hours correctly)
                           const daysToAddMs = daysToAdd * 24 * 60 * 60 * 1000;
                           const minEndDateFromStart = new Date(startDateTime.getTime() + daysToAddMs);
                           
                           if (selectedDateTime < minimum) {
                             toast.error(isAutoEmail ? "Start time must be at least 4 hours from now" : "Start time must be current time or later");
                             setEndDate("");
                             setEndTime("17:00");
                           } else if (selectedDateTime <= startDateTime) {
                             toast.error("End time must be after start time");
                             setEndDate("");
                             setEndTime("17:00");
                           } else {
                             // Check if end datetime is at least the dynamic dayinterval from start datetime
                             // Compare full datetime (not just date) to handle cases where time matters
                             if (selectedDateTime < minEndDateFromStart) {
                               toast.error(`End date must be at least ${getIntervalDisplayString(dynamicDayInterval)} after the start date`);
                               setEndDate("");
                               setEndTime("17:00");
                             }
                           }
                        }
                      }}
                    />
                    
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowScheduleDialog(false);
                setJourneyToSchedule(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={() => handleSchedulePublish(true)}
              disabled={publishing}
              style={{
                backgroundColor: '#17a2b8',
                borderColor: '#17a2b8'
              }}
            >
              Publish
            </Button>
                     </Modal.Footer>
              </Modal>

              
              {/* Unpublish Warning Modal */}
              <Modal
          show={stepsModalOpen}
          onHide={() => setStepsModalOpen(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Steps List</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <table className="table table-bordered table-striped table-sm">
              <thead className="table-light">
                <tr>
                  <th scope="col">Sr. No</th>
                  <th scope="col">Name</th>
                  <th scope="col">Status</th>
                  <th scope="col">Date</th>
                </tr>
              </thead>
                <tbody>
                 {journeyToSteps && journeyToSteps.length > 0 ? (
                   journeyToSteps.map((step, index) => (
                     <tr key={index}>
                       <td>{index + 1}</td>
                       <td>{step.node}</td>
                       <td>{step.status}</td>
                       <td>{step.scheduled_on}</td>
                     </tr>
                   ))
                 ) : (
                   <tr>
                     <td colSpan={4} className="text-center">No steps found</td>
                   </tr>
                 )}
                 

              </tbody>
            </table>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setStepsModalOpen(false)}
            >
              OK
            </Button>
          </Modal.Footer>
        </Modal>



      </div>
      </>
    );
  };
  
  export default JourneyListing;